import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  message,
  Typography,
  Spin,
  Space,
  Tag,
  Popconfirm,
  Tooltip,
  Empty,
  Badge,
  Divider,
  DatePicker,
  Progress,
  Drawer,
  List,
  Avatar,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CrownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  CalendarOutlined,
  ReloadOutlined,
  EyeOutlined,
  GiftOutlined,
  HistoryOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import {
  getMembers,
  createMember,
  updateMember,
  deleteMember,
  getMemberUsageHistory,
} from "../../services/memberService";
import { getActivePlans } from "../../services/planService";
import moment from "moment";
import { toast } from "react-toastify";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;

export default function MemberManagement() {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [activePlans, setActivePlans] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [usageDrawerVisible, setUsageDrawerVisible] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const { companyId } = useParams();

  useEffect(() => {
    fetchMembers();
    fetchActivePlans();
  }, []);

  useEffect(() => {
    filterMembers();
  }, [members, searchText, dateRange]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const res = await getMembers({ companyId });
      setMembers(res?.data || []);
    } catch (err) {
      message.error("Failed to fetch members");
    } finally {
      setLoading(false);
    }
  };

  const fetchActivePlans = async () => {
    try {
      const res = await getActivePlans({ companyId });

      setActivePlans(res?.data?.data || []);
    } catch (err) {
      console.error("Error fetching plans", err);
    }
  };

  const fetchUsageHistory = async (memberId) => {
    try {
      const res = await getMemberUsageHistory(memberId);
      setUsageHistory(res?.data || []);
    } catch (err) {
      message.error("Failed to fetch usage history");
    }
  };

  const filterMembers = () => {
    let filtered = [...members];

    // Search filter
    if (searchText) {
      const keyword = searchText.toLowerCase();
      filtered = filtered.filter(
        (member) =>
          member.name?.toLowerCase().includes(keyword) ||
          member.email?.toLowerCase().includes(keyword) ||
          member.phone?.includes(keyword)
      );
    }

    // Date range filter
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      filtered = filtered.filter((member) => {
        const createdDate = moment(member.createdAt);
        return createdDate.isBetween(start, end, "day", "[]");
      });
    }

    setFilteredMembers(filtered);
  };

  const handleAddMember = () => {
    setEditingMember(null);
    form.resetFields();
    setSelectedPlanId(null);
    setModalVisible(true);
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setSelectedPlanId(member.membershipPlan?.planId);

    form.setFieldsValue({
      name: member.name,
      email: member.email,
      phone: member.phone,
      address: member.address,
      planId: member.membershipPlan?.planId,
      status: member.status,
    });

    setModalVisible(true);
  };

  const handleDeleteMember = async (memberId) => {
    try {
      await deleteMember(memberId);
      setMembers(members.filter((member) => member._id !== memberId));
      toast.success("Member deleted successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete member");
    }
  };

  const handleViewUsage = async (member) => {
    setSelectedMember(member);
    await fetchUsageHistory(member._id);
    setUsageDrawerVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);

      const payload = {
        ...values,
        companyId,
      };

      if (editingMember) {
        await updateMember(editingMember._id, payload);
        toast.success("Member updated successfully");
      } else {
        await createMember(payload);
        toast.success("Member created successfully");
      }

      await fetchMembers();
      setModalVisible(false);
      form.resetFields();
      setEditingMember(null);
      setSelectedPlanId(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save member");
    } finally {
      setSubmitting(false);
    }
  };

  const getSelectedPlan = () => {
    if (!selectedPlanId) return null;
    return activePlans.find((plan) => plan._id === selectedPlanId);
  };

  const getMembershipStatus = (member) => {
    if (!member.membershipPlan || !member.membershipPlan.isActive) {
      return { status: "No Plan", color: "default" };
    }

    const expiryDate = moment(member.membershipPlan.expiryDate);
    const today = moment();

    if (expiryDate.isBefore(today)) {
      return { status: "Expired", color: "error" };
    }

    const daysLeft = expiryDate.diff(today, "days");
    if (daysLeft <= 7) {
      return { status: "Expiring Soon", color: "warning" };
    }

    return { status: "Active", color: "success" };
  };

  const getUsagePercentage = (serviceUsage) => {
    if (!serviceUsage || !serviceUsage.totalAllowed) return 0;
    return Math.round((serviceUsage.used / serviceUsage.totalAllowed) * 100);
  };

  const columns = [
    {
      title: "Member Info",
      key: "info",
      render: (_, record) => (
        <Space>
          <Avatar size={48} icon={<UserOutlined />} style={{ backgroundColor: "#1890ff" }} />
          <div>
            <Text strong style={{ display: "block" }}>
              {record.name}
            </Text>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.phone}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => (
        <Space>
          <MailOutlined style={{ color: "#1890ff" }} />
          <Text>{email || "N/A"}</Text>
        </Space>
      ),
    },
    {
      title: "Membership Plan",
      key: "plan",
      render: (_, record) => {
        if (!record.membershipPlan || !record.membershipPlan.planName) {
          return <Tag color="default">No Plan</Tag>;
        }

        const { status, color } = getMembershipStatus(record);
        return (
          <Space direction="vertical" size={0}>
            <Space>
              <CrownOutlined style={{ color: "#faad14" }} />
              <Text strong>{record.membershipPlan.planName}</Text>
            </Space>
            <Tag color={color} icon={status === "Active" ? <CheckCircleOutlined /> : null}>
              {status}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: "Validity",
      key: "validity",
      render: (_, record) => {
        if (!record.membershipPlan || !record.membershipPlan.expiryDate) {
          return <Text type="secondary">-</Text>;
        }

        return (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: "12px" }}>
              <CalendarOutlined /> Expires: {moment(record.membershipPlan.expiryDate).format("DD MMM YYYY")}
            </Text>
            <Text type="secondary" style={{ fontSize: "11px" }}>
              {moment(record.membershipPlan.expiryDate).fromNow()}
            </Text>
          </Space>
        );
      },
    },
    {
      title: "Service Usage",
      key: "usage",
      render: (_, record) => {
        if (!record.membershipPlan || !record.membershipPlan.serviceUsage) {
          return <Text type="secondary">-</Text>;
        }

        const totalUsed = record.membershipPlan.serviceUsage.reduce(
          (sum, s) => sum + s.used,
          0
        );
        const totalAllowed = record.membershipPlan.serviceUsage.reduce(
          (sum, s) => sum + s.totalAllowed,
          0
        );

        return (
          <Space direction="vertical" size={0} style={{ width: "100%" }}>
            <Text strong>
              {totalUsed} / {totalAllowed} used
            </Text>
            <Progress
              percent={Math.round((totalUsed / totalAllowed) * 100)}
              size="small"
              status={totalUsed >= totalAllowed ? "exception" : "active"}
            />
          </Space>
        );
      },
    },
    {
      title: "Joined Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={status === "active" ? "success" : "error"}
          icon={status === "active" ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        >
          {status === "active" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Usage">
            <Button
              type="primary"
              ghost
              icon={<EyeOutlined />}
              onClick={() => handleViewUsage(record)}
              size="small"
              disabled={!record.membershipPlan}
            />
          </Tooltip>
          <Tooltip title="Edit Member">
            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={() => handleEditMember(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Delete Member">
            <Popconfirm
              title="Delete Member"
              description="Are you sure you want to delete this member?"
              onConfirm={() => handleDeleteMember(record._id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />} size="small" />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        padding: "24px",
      }}
    >
      <div style={{ maxWidth: "1600px", margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "32px",
            marginBottom: "24px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Title
                level={2}
                style={{
                  margin: 0,
                  marginBottom: "8px",
                  background: "linear-gradient(45deg, #1890ff, #722ed1)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: "32px",
                }}
              >
                <UserOutlined style={{ marginRight: "12px" }} />
                Member Management
              </Title>
              <Text type="secondary" style={{ fontSize: "16px" }}>
                Manage members and their membership plans
              </Text>
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchMembers}
                  size="large"
                  style={{ borderRadius: "12px" }}
                >
                  Refresh
                </Button>
                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  onClick={handleAddMember}
                  style={{
                    borderRadius: "12px",
                    background: "linear-gradient(45deg, #1890ff, #722ed1)",
                    border: "none",
                    boxShadow: "0 4px 12px rgba(24,144,255,0.4)",
                    height: "48px",
                    fontSize: "16px",
                  }}
                >
                  Add New Member
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Filters */}
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "24px",
            marginBottom: "24px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12} lg={10}>
              <Search
                placeholder="Search by name, email, or phone..."
                size="large"
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                style={{ borderRadius: "12px" }}
              />
            </Col>
            <Col xs={24} md={12} lg={8}>
              <RangePicker
                size="large"
                style={{ width: "100%", borderRadius: "12px" }}
                format="DD/MM/YYYY"
                onChange={(dates) => setDateRange(dates)}
                placeholder={["Start Date", "End Date"]}
              />
            </Col>
            <Col xs={24} md={24} lg={6}>
              <div
                style={{
                  background: "linear-gradient(45deg, #1890ff, #722ed1)",
                  color: "white",
                  padding: "12px 16px",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  textAlign: "center",
                }}
              >
                Total Members: {filteredMembers.length}
              </div>
            </Col>
          </Row>
        </div>

        {/* Members Table */}
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "24px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          }}
        >
          <Table
            columns={columns}
            dataSource={filteredMembers}
            rowKey="_id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} members`,
            }}
            scroll={{ x: 1400 }}
            locale={{
              emptyText: (
                <Empty
                  description="No members found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddMember}>
                    Add First Member
                  </Button>
                </Empty>
              ),
            }}
          />
        </div>
      </div>

      {/* Create/Edit Member Modal */}
      <Modal
        title={
          <div style={{ fontSize: "20px", fontWeight: "bold" }}>
            <UserOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
            {editingMember ? "Edit Member" : "Add New Member"}
          </div>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingMember(null);
          setSelectedPlanId(null);
        }}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Title level={4} style={{ color: "#1890ff", marginBottom: "16px" }}>
            Personal Information
          </Title>

          <Form.Item
            name="name"
            label={
              <Text strong>
                <UserOutlined style={{ marginRight: "4px" }} />
                Full Name
              </Text>
            }
            rules={[{ required: true, message: "Please enter member name" }]}
          >
            <Input placeholder="Enter full name" size="large" style={{ borderRadius: "8px" }} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label={
                  <Text strong>
                    <PhoneOutlined style={{ marginRight: "4px" }} />
                    Phone Number
                  </Text>
                }
                rules={[
                  { required: true, message: "Please enter phone number" },
                  { len: 10, message: "Phone must be 10 digits" },
                ]}
              >
                <Input
                  placeholder="10-digit phone"
                  size="large"
                  style={{ borderRadius: "8px" }}
                  maxLength={10}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label={
                  <Text strong>
                    <MailOutlined style={{ marginRight: "4px" }} />
                    Email
                  </Text>
                }
                rules={[{ type: "email", message: "Please enter valid email" }]}
              >
                <Input
                  placeholder="email@example.com"
                  size="large"
                  style={{ borderRadius: "8px" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label={<Text strong>Address (Optional)</Text>}
          >
            <Input.TextArea
              placeholder="Enter address"
              rows={2}
              style={{ borderRadius: "8px" }}
            />
          </Form.Item>

          <Divider />

          <Title level={4} style={{ color: "#1890ff", marginBottom: "16px" }}>
            <CrownOutlined style={{ marginRight: "8px" }} />
            Membership Plan
          </Title>

          <Form.Item
            name="planId"
            label={<Text strong>Select Plan (Optional)</Text>}
          >
            <Select
              placeholder="Select a membership plan"
              size="large"
              style={{ borderRadius: "8px" }}
              allowClear
              onChange={(value) => setSelectedPlanId(value)}
            >
              {activePlans?.map((plan) => (
                <Option key={plan._id} value={plan._id}>
                  <Space direction="vertical" size={0}>
                    <Text  strong>{plan.name}</Text>
                    {/* <Text  type="secondary" style={{ fontSize: "12px",marginTop:'-20px' }}>
                      ₹{plan.price} - Valid for {plan.validityValue}{" "}
                      {plan.validityType === "months" ? "months" : "days"}
                    </Text> */}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedPlanId && getSelectedPlan() && (
            <div
              style={{
                background: "#f0f9ff",
                padding: "16px",
                borderRadius: "12px",
                marginBottom: "16px",
              }}
            >
              <Title level={5}>Plan Details</Title>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text>
                  <strong>Plan:</strong> {getSelectedPlan().name}
                </Text>
                <Text>
                  <strong>Price:</strong> ₹{getSelectedPlan().price}
                </Text>
                <Text>
                  <strong>Services Included:</strong>
                </Text>
                <List
                  size="small"
                  dataSource={getSelectedPlan().serviceList || []}
                  renderItem={(item) => (
                    <List.Item style={{ padding: "4px 0", border: "none" }}>
                      <Space>
                        <CheckCircleOutlined style={{ color: "#52c41a" }} />
                        <Text>
                          {item.serviceId?.name || "Service"} - {item.repeatCount}x
                        </Text>
                        {item.isComplimentary && (
                          <Tag color="green" icon={<GiftOutlined />}>
                            Free
                          </Tag>
                        )}
                      </Space>
                    </List.Item>
                  )}
                />
              </Space>
            </div>
          )}

          <Form.Item
            name="status"
            label={<Text strong>Status</Text>}
            initialValue="active"
            rules={[{ required: true }]}
          >
            <Select size="large" style={{ borderRadius: "8px" }}>
              <Option value="active">
                <Space>
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />
                  <span>Active</span>
                </Space>
              </Option>
              <Option value="inactive">
                <Space>
                  <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                  <span>Inactive</span>
                </Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginTop: "32px", marginBottom: 0 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                size="large"
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingMember(null);
                  setSelectedPlanId(null);
                }}
                style={{ borderRadius: "8px" }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitting}
                style={{
                  borderRadius: "8px",
                  background: "linear-gradient(45deg, #1890ff, #722ed1)",
                  border: "none",
                  minWidth: "120px",
                }}
              >
                {submitting
                  ? editingMember
                    ? "Updating..."
                    : "Creating..."
                  : editingMember
                  ? "Update Member"
                  : "Create Member"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Usage History Drawer */}
      <Drawer
        title={
          <div>
            <Space>
              <Avatar size={40} icon={<UserOutlined />} style={{ backgroundColor: "#1890ff" }} />
              <div>
                <Text strong style={{ fontSize: "16px", display: "block" }}>
                  {selectedMember?.name}
                </Text>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Service Usage History
                </Text>
              </div>
            </Space>
          </div>
        }
        width={600}
        onClose={() => {
          setUsageDrawerVisible(false);
          setSelectedMember(null);
          setUsageHistory([]);
        }}
        open={usageDrawerVisible}
      >
        {selectedMember?.membershipPlan && (
          <>
            {/* Plan Summary */}
            <Card
              style={{
                marginBottom: "24px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
              }}
            >
              <Space direction="vertical" style={{ width: "100%", color: "white" }}>
                <Text strong style={{ color: "white", fontSize: "16px" }}>
                  <CrownOutlined /> {selectedMember.membershipPlan.planName}
                </Text>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: "12px" }}>
                      Valid Until
                    </Text>
                    <div style={{ color: "white", fontWeight: "bold" }}>
                      {moment(selectedMember.membershipPlan.expiryDate).format("DD MMM YYYY")}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: "12px" }}>
                      Status
                    </Text>
                    <div>
                      <Tag color={getMembershipStatus(selectedMember).color} style={{ marginTop: "4px" }}>
                        {getMembershipStatus(selectedMember).status}
                      </Tag>
                    </div>
                  </Col>
                </Row>
              </Space>
            </Card>

            {/* Service Usage */}
            <Title level={5} style={{ marginBottom: "16px" }}>
              <HistoryOutlined /> Service Usage
            </Title>
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              {selectedMember.membershipPlan.serviceUsage?.map((service, index) => (
                <Card
                  key={index}
                  size="small"
                  style={{ borderRadius: "8px" }}
                  bodyStyle={{ padding: "12px" }}
                >
                  <Row align="middle" justify="space-between">
                    <Col flex="auto">
                      <Space direction="vertical" size={0}>
                        <Text strong>{service.serviceName}</Text>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {service.used} / {service.totalAllowed} used
                          {service.isComplimentary && (
                            <Tag
                              color="green"
                              icon={<GiftOutlined />}
                              style={{ marginLeft: "8px" }}
                              size="small"
                            >
                              Free
                            </Tag>
                          )}
                        </Text>
                      </Space>
                    </Col>
                    <Col>
                      <div style={{ width: "120px" }}>
                        <Progress
                          percent={getUsagePercentage(service)}
                          size="small"
                          status={service.used >= service.totalAllowed ? "exception" : "active"}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card>
              ))}
            </Space>

            <Divider />

            {/* Usage History */}
            <Title level={5} style={{ marginBottom: "16px" }}>
              <ClockCircleOutlined /> Recent Activity
            </Title>
            {usageHistory.length > 0 ? (
              <List
                dataSource={usageHistory}
                renderItem={(item) => (
                  <List.Item style={{ padding: "12px 0" }}>
                    <List.Item.Meta
                      avatar={<CheckCircleOutlined style={{ color: "#52c41a", fontSize: "20px" }} />}
                      title={item.serviceName}
                      description={
                        <Space direction="vertical" size={0}>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            Used on {moment(item.usedAt).format("DD MMM YYYY, hh:mm A")}
                          </Text>
                          {item.staffName && (
                            <Text type="secondary" style={{ fontSize: "11px" }}>
                              Staff: {item.staffName}
                            </Text>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No usage history yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </>
        )}

        {!selectedMember?.membershipPlan && (
          <Empty description="No active membership plan" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Drawer>
    </div>
  );
}